import React from "react";

import type { EndorsementResponse } from "@tm/insurance/api";
import { FlexComp, FormattedText, ImageComponent, Spinner } from "@tm/static";
import { theme } from "@tm/utility/global";
import { ORDER_DETAIL_V2_ASSETS } from "../../constants";
import type { EndorsementView } from "../../types";

// ── Props ───────────────────────────────────────────────────────────────

export interface InfoModalContentProps {
  viewType: EndorsementView;
  data: EndorsementResponse | null;
  /** Called when the user taps "Retry" on an error view */
  onRetry?: () => void;
  /** Fallback icon shown on success when the API doesn't provide one */
  defaultSuccessIcon?: string;
  /**
   * Optional render prop for the CTA area.
   * Receives endorsementData so the caller can build a styled link/button.
   * If not provided, a default link button is rendered for success views.
   */
  renderAction?: (data: EndorsementResponse) => React.ReactNode;
}

// ── Static styles ───────────────────────────────────────────────────────

const resultContentStyle: React.CSSProperties = {
  textAlign: "center",
  color: theme.greyText,
  fontSize: "14px",
  lineHeight: "1.4",
};

const resultTitleStyle: React.CSSProperties = {
  ...resultContentStyle,
  fontWeight: 700,
  fontSize: "18px",
  marginBottom: "8px",
};

const resultSubtitleStyle: React.CSSProperties = {
  ...resultContentStyle,
  color: theme.greyText7,
  marginBottom: "4px",
};

const resultDescStyle: React.CSSProperties = {
  ...resultContentStyle,
  color: theme.greyText7,
  fontSize: "13px",
  marginBottom: "16px",
};

// ── Helpers ──────────────────────────────────────────────────────────────

const HtmlBlock: React.FC<{
  html: string | undefined;
  style: React.CSSProperties;
}> = ({ html, style }) =>
  html ? (
    <div style={style}>
      <FormattedText value={{ html }} />
    </div>
  ) : null;

// ── Component ───────────────────────────────────────────────────────────

export const InfoModalContent: React.FC<InfoModalContentProps> = ({
  viewType,
  data,
  onRetry,
  defaultSuccessIcon,
  renderAction,
}) => {
  if (!data) return null;

  const { icon, title, subtitle, desc } = data;

  if (viewType === "success") {
    return (
      <FlexComp
        flexDirection="column"
        alignItems="center"
        gap="0"
        margin="0 0 24px"
      >
        <ImageComponent
          src={
            icon || defaultSuccessIcon || ORDER_DETAIL_V2_ASSETS.SUCCESS_ICON
          }
          width="56"
          height="56"
          alt="Success"
          margin="0 auto 16px"
        />
        <HtmlBlock html={title} style={resultTitleStyle} />
        <HtmlBlock html={subtitle} style={resultSubtitleStyle} />
        <HtmlBlock html={desc} style={resultDescStyle} />
        {renderAction ? renderAction(data) : null}
      </FlexComp>
    );
  }

  if (viewType === "error") {
    return (
      <FlexComp
        flexDirection="column"
        alignItems="center"
        gap="0"
        margin="0 0 24px"
      >
        <HtmlBlock html={title} style={resultTitleStyle} />
        <HtmlBlock html={subtitle} style={resultSubtitleStyle} />
        <HtmlBlock html={desc} style={resultDescStyle} />
        {renderAction ? (
          renderAction(data)
        ) : onRetry && data.cta?.isEnabled !== false && data.cta?.label ? (
          <button
            onClick={onRetry}
            style={{ marginTop: "16px", cursor: "pointer" }}
          >
            <FormattedText value={data.cta.label} />
          </button>
        ) : null}
      </FlexComp>
    );
  }

  if (viewType === "generating") {
    return (
      <FlexComp
        flexDirection="column"
        alignItems="center"
        gap="0"
        margin="0 0 24px"
      >
        <HtmlBlock html={title} style={resultTitleStyle} />
        <HtmlBlock html={subtitle} style={resultSubtitleStyle} />
        <HtmlBlock html={desc} style={resultDescStyle} />
        <FlexComp margin="16px 0 0">
          <Spinner
            spinnerWidth={32}
            spinnerColor={theme.blueText}
            thickness={2}
          />
        </FlexComp>
      </FlexComp>
    );
  }

  return null;
};
